<template lang="pug">
    PrintablePanel
      ICEAlert
      PageHeader Store Activity Report
      .row
        .col
          h2
            | {{ storeInfo.pharmacyName }}
            span(v-if="storeInfo.inactive")  - Inactive
          h4
            div {{ storeInfo.phone }}
            div {{ storeInfo.address1 }}
            div(v-if="storeInfo.address2") {{ storeInfo.address2 }}
            div {{ storeInfo.city }}, {{ storeInfo.stateCd }} {{ storeInfo.postalCode }}
        .col.text-right(v-if="!printable")
          router-link.btn.btn-primary(:to="{query: {print: true}}" target="_blank") Print
      .row.mt-2
        .col
          h4 {{ complianceDetailLine }}
          em.body-1 To get a detailed history report based on a transaction, select the Transaction ID.
      .row.mt-5
        .col
          Datatable(:columns="columns",:entries="storeActivityResults",:perPage="6")
            template(slot-scope="props")
              tr.adjust-padding
                td.body-3(style="width: 125px;")
                  div
                    router-link(:to="{name: 'person_summary', params: {id: props.entry.subjectSid}}") (N) {{ props.entry.transactionSid }}
                  div (S) {{ props.entry.pharmacyTrxId }}
                  div {{ props.entry.transactionCd }}
                td.body-3 {{ props.entry.transactionDt }}
                td.body-3
                  div {{ props.entry.firstName }} {{ props.entry.lastName }}
                  div {{ props.entry.address1 }}
                  div {{ props.entry.address2 }}
                  div {{ props.entry.city }}, {{ props.entry.stateCd }} {{ props.entry.postalCode }}
                  div DOB: {{ props.entry.birthDt }}
                td.body-3 {{ props.entry.creationUser }}
                td.body-3
                  div Number: {{ props.entry.idNbr }}
                  div {{ idType(props.entry.idTypeCd) }}
                  div {{ props.entry.idStateCd }}
                  div Expires: {{ props.entry.idExpDt }}
                td.body-3.product-cell
                  .product-container
                    .row.product-header
                      .col.product-description Product Description
                      .col Qty
                      .col Pills/mL
                      .col PSE(g)
                    .row.products(v-for="product in props.entry.products")
                      .col.product-description {{ product.productDescription }}
                      .col {{ product.boxes }}
                      .col {{ product.pills }}
                      .col {{ product.grams.toFixed(2) }}
                    .row.product-footer
                      .col.product-description Transaction Totals
                      .col {{ sum(props.entry.products.map(r => r.boxes)) }}
                      .col {{ sum(props.entry.products.map(r => r.pills)) }}
                      .col {{ sum(props.entry.products.map(r => r.grams)).toFixed(2) }}
              tr(v-if="criteria.signatures && props.entry.imageName")
                td(colspan="6")
                    img.img-fluid(
                      v-if="signatureUrls[props.entry.imageName]"
                      :src="'data:image/png;base64, ' + signatureUrls[props.entry.imageName]"
                      style="border: 1px solid #e6e6e6; max-height: 6rem;"
                      @load="signaturesLoaded.push(props.entry.imageName)"
                      @error="signaturesLoaded.push(props.entry.imageName)")
</template>

<script>
import PageHeader from '@/components/PageHeader'
import Datatable from '@/components/Datatable'
import PrintablePanel from '@/components/PrintablePanel'
import axios from '@/lib/axios'
import { formItem } from '@/lib/formstore'
import { globalError, idType } from '@/lib/utils'
import { format } from 'date-fns/esm'
import store from '@/store'

function getData(criteria) {
  let p1 = axios()
    .get('/api/store_activity/search', criteria)
    .catch(err => globalError(err))
  let p2 = axios()
    .get('/api/misc/store_info', { params: { sid: criteria.pharmacySid } })
    .catch(err => globalError(err))
  return Promise.all([p1, p2])
}

export default {
  name: 'StoreActivityReport',
  data() {
    return {
      columns: [
        'Transaction ID(s)<br>(N)PLEx<br>(S)tore',
        'Transaction Date',
        'Name / Address / DOB',
        'Pharmacy Technician',
        'ID Info',
        'Product',
      ],
      storeInfo: {},
      criteria: {},
      storeActivityResults: [],
      signatureUrls: {},
      signaturesLoaded: [],
    }
  },
  computed: {
    complianceDetailLine() {
      let sar = this.criteria
      return `Compliance Detail Report ${
        sar.signatures ? '' : '(without signatures)'
      } for ${sar.fromDate}-${sar.toDate}.`
    },
    uniqLoaded() {
      return [...new Set(this.signaturesLoaded)]
    },
    uniqUrls() {
      return [...new Set(Object.keys(this.signatureUrls))]
    },
  },
  methods: {
    sum(vals = []) {
      return vals.reduce((acc, val) => acc + val, 0)
    },
    format,
    idType,
  },
  async beforeRouteEnter(to, _from, next) {
    try {
      let results = await axios().get('/api/reports/link', {
        params: { id: to.params.id },
      })
      let store_resp = await axios().get('/api/misc/store_info', {
        params: { sid: results.data.criteria.pharmacySid },
      })
      next(vm => {
        vm.storeActivityResults = results.data.results
        vm.storeInfo = store_resp.data
        vm.criteria = results.data.criteria
      })
    } catch (err) {
      globalError(err)
    }
  },
  watch: {
    storeActivityResults(val) {
      let promises = []
      for (let trx of val) {
        if (trx.imageName) {
          let p = axios()
            .get('/api/misc/signature', {
              params: { path: encodeURIComponent(trx.imageName) },
            })
            .then(resp => {
              this.$set(this.signatureUrls, trx.imageName, resp.data.encoded_img)
            })
            .catch(err => globalError(err))
          promises.push(p)
        }
      }
      Promise.all(promises).finally(() => {
        let crit =
          this.printable &&
          (this.uniqUrls.length === 0 || !this.criteria.signatures)
        if (crit) window.print()
      })
    },
    signaturesLoaded(val) {
      // When all of the signatures are loaded, set the imagesLoaded flag
      let uniqNames = [...new Set(val)]
      let uniqUrls = [...new Set(Object.keys(this.signatureUrls))]
      if (this.printable && uniqNames.length === uniqUrls.length) window.print()
    },
  },
  components: {
    PageHeader,
    Datatable,
    PrintablePanel,
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/bodies';
@import '../assets/styles/breakpoints';

.product-cell {
  width: 300px;
}

tr.adjust-padding {
  td {
    padding: 3px;
  }
}
.product-container {
  @extend .body-3;
  text-align: right;
  .row {
    margin: 0;
    .col {
      padding: 0;
    }
  }
  .product-header {
    font-weight: bold;
    border-bottom: 1px solid #e6e6e6;
    margin-left: 0;
  }
  .product-footer {
    border-top: 1px solid #e6e6e6;
    .product-description {
      font-style: italic;
    }
  }
  .product-description {
    flex-grow: 2;
    text-align: left;
  }
}
</style>
